import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '../lib/supabase';

/**
 * Hook to sync data in real-time from Supabase
 * Automatically subscribes to INSERT, UPDATE, DELETE events on specified tables
 */
export const useRealtimeSync = (table, { onInsert, onUpdate, onDelete, filter } = {}) => {
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [error, setError] = useState(null);
  const subscriptionRef = useRef(null);
  const retryTimerRef = useRef(null);
  const retryCountRef = useRef(0);
  const MAX_RETRIES = 3;

  // Stable refs for callbacks — prevents effect from re-running when inline functions change
  const onInsertRef = useRef(onInsert);
  const onUpdateRef = useRef(onUpdate);
  const onDeleteRef = useRef(onDelete);

  useEffect(() => { onInsertRef.current = onInsert; }, [onInsert]);
  useEffect(() => { onUpdateRef.current = onUpdate; }, [onUpdate]);
  useEffect(() => { onDeleteRef.current = onDelete; }, [onDelete]);

  const subscribe = useCallback(() => {
    if (!supabase) {
      console.warn('[Realtime] Supabase client not available - skipping realtime subscription');
      return;
    }
    if (!table) return;
    if (typeof supabase?.channel !== 'function') return;

    // Clean up any existing subscription first
    if (subscriptionRef?.current && typeof supabase?.removeChannel === 'function') {
      try { supabase?.removeChannel(subscriptionRef?.current); } catch (_) {}
      subscriptionRef.current = null;
    }

    const channelName = `realtime:${table}:${Date.now()}`;

    try {
      const channel = supabase?.channel(channelName);
      if (!channel || typeof channel?.on !== 'function' || typeof channel?.subscribe !== 'function') {
        return;
      }

      let ch = channel;

      if (onInsertRef?.current) {
        ch = ch?.on('postgres_changes', {
          event: 'INSERT', schema: 'public', table,
          ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
        }, (payload) => { onInsertRef?.current?.(payload?.new); });
      }

      if (onUpdateRef?.current) {
        ch = ch?.on('postgres_changes', {
          event: 'UPDATE', schema: 'public', table,
          ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
        }, (payload) => { onUpdateRef?.current?.(payload?.new, payload?.old); });
      }

      if (onDeleteRef?.current) {
        ch = ch?.on('postgres_changes', {
          event: 'DELETE', schema: 'public', table,
          ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
        }, (payload) => { onDeleteRef?.current?.(payload?.old); });
      }

      ch?.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          retryCountRef.current = 0;
          setIsSubscribed(true);
          setError(null);
        } else if (status === 'CHANNEL_ERROR') {
          setIsSubscribed(false);
          if (retryCountRef?.current < MAX_RETRIES) {
            const delay = Math.pow(2, retryCountRef?.current) * 2000;
            retryCountRef.current += 1;
            console.warn(`[Realtime] Channel error for ${table}, retrying in ${delay}ms (attempt ${retryCountRef?.current}/${MAX_RETRIES})`);
            retryTimerRef.current = setTimeout(() => subscribe(), delay);
          } else {
            console.warn(`[Realtime] Could not subscribe to ${table} after ${MAX_RETRIES} attempts — realtime updates disabled for this session`);
            setError(`Realtime updates unavailable for ${table}`);
          }
        } else if (status === 'TIMED_OUT') {
          setIsSubscribed(false);
          console.warn(`[Realtime] Subscription timed out for ${table}`);
        } else if (status === 'CLOSED') {
          setIsSubscribed(false);
        }
      });

      subscriptionRef.current = ch;
    } catch (err) {
      console.warn(`[Realtime] Error setting up subscription for ${table}:`, err?.message);
    }
  }, [table, filter?.column, filter?.value]); // stable — callbacks handled via refs

  useEffect(() => {
    retryCountRef.current = 0;
    subscribe();

    return () => {
      clearTimeout(retryTimerRef?.current);
      if (subscriptionRef?.current && supabase && typeof supabase?.removeChannel === 'function') {
        try { supabase?.removeChannel(subscriptionRef?.current); } catch (_) {}
        subscriptionRef.current = null;
      }
      setIsSubscribed(false);
    };
  }, [subscribe]);

  return { isSubscribed, error };
};

/**
 * Hook to sync multiple tables simultaneously
 */
export const useMultiTableRealtimeSync = (subscriptions = []) => {
  const [activeSubscriptions, setActiveSubscriptions] = useState({});
  const [errors, setErrors] = useState({});
  const channelsRef = useRef([]);

  // Stable ref for subscriptions array contents
  const subscriptionsRef = useRef(subscriptions);
  useEffect(() => { subscriptionsRef.current = subscriptions; }, [subscriptions]);

  useEffect(() => {
    // Clear any existing channels
    channelsRef?.current?.forEach((channel) => {
      try {
        if (supabase && typeof supabase?.removeChannel === 'function') {
          supabase?.removeChannel(channel);
        }
      } catch (_) {}
    });
    channelsRef.current = [];

    if (!supabase || typeof supabase?.channel !== 'function') return;
    if (!subscriptionsRef?.current || subscriptionsRef?.current?.length === 0) return;

    subscriptionsRef?.current?.forEach(({ table, onInsert, onUpdate, onDelete, filter }) => {
      if (!table) return;

      try {
        const channelName = `realtime:${table}:${Date.now()}-${Math.random()}`;
        const channel = supabase?.channel(channelName);

        if (!channel || typeof channel?.on !== 'function' || typeof channel?.subscribe !== 'function') {
          return;
        }

        let ch = channel;

        if (onInsert) {
          ch = ch?.on('postgres_changes', {
            event: 'INSERT', schema: 'public', table,
            ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
          }, (payload) => { onInsert(payload?.new); });
        }

        if (onUpdate) {
          ch = ch?.on('postgres_changes', {
            event: 'UPDATE', schema: 'public', table,
            ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
          }, (payload) => { onUpdate(payload?.new, payload?.old); });
        }

        if (onDelete) {
          ch = ch?.on('postgres_changes', {
            event: 'DELETE', schema: 'public', table,
            ...(filter && { filter: `${filter?.column}=eq.${filter?.value}` })
          }, (payload) => { onDelete(payload?.old); });
        }

        ch?.subscribe((status) => {
          if (status === 'SUBSCRIBED') {
            setActiveSubscriptions(prev => ({ ...prev, [table]: true }));
            setErrors(prev => { const n = { ...prev }; delete n?.[table]; return n; });
          } else if (status === 'CHANNEL_ERROR') {
            console.warn(`[Realtime] Channel error for ${table} — realtime updates may be unavailable`);
            setActiveSubscriptions(prev => ({ ...prev, [table]: false }));
          } else if (status === 'TIMED_OUT') {
            setActiveSubscriptions(prev => ({ ...prev, [table]: false }));
          } else if (status === 'CLOSED') {
            setActiveSubscriptions(prev => ({ ...prev, [table]: false }));
          }
        });

        channelsRef?.current?.push(ch);
      } catch (err) {
        console.warn(`[Realtime] Error setting up realtime for ${table}:`, err?.message);
      }
    });

    return () => {
      channelsRef?.current?.forEach((channel) => {
        try {
          if (supabase && typeof supabase?.removeChannel === 'function') {
            supabase?.removeChannel(channel);
          }
        } catch (_) {}
      });
      channelsRef.current = [];
      setActiveSubscriptions({});
    };
  }, [subscriptions?.length]);

  return { activeSubscriptions, errors };
};